/**
 * Shared state for the appointment-limit upsell.
 *
 * One provider owns four things so the side-menu card, the dialog and the
 * micro-app plumbing can never disagree:
 *
 * 1. **Eligibility** — WSB merchant, beta toggle on, and a positive allowance
 *    from the public API.
 * 2. **Pricing** — requested from the One.com parent window by postMessage, and
 *    delivered the same way. Gates the side-menu card only, since that is the
 *    one surface whose copy quotes an amount; the dialogs render without it.
 * 3. **Usage** — allowance and spend, re-read as appointments are created; see
 *    ./usage.ts.
 * 4. **Dialog visibility** — including the once-per-session rule.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useAppSelector } from '../../shared/hooks/use-app-selector';
import { usePostMessageChannel } from '../../shared/hooks/usePostMessageChannel';
import { BETA_TOGGLES } from '../beta/toggles';
import { useLocalStorageState } from 'ahooks';
import {
  DIALOG_SEEN_STORAGE_KEY,
  PRICING_MESSAGE_TYPE,
  PRICING_REQUEST_MESSAGE_TYPE,
} from './constants';
import { parsePricing, type UpgradePricing } from './pricing';
import { classifyUsage, type AppointmentUsage, type UsageState } from './usage';
import { resolveUpgradeEligibility } from './eligibility';

interface BookingsUpgradeContextValue {
  /** Pricing from the parent window; `null` until the message arrives. */
  pricing: UpgradePricing | null;
  /** Allowance and spend; `null` for merchants the upsell does not apply to. */
  usage: AppointmentUsage | null;
  /** Which of the three states the merchant is in. */
  usageState: UsageState;
  /**
   * True when the side-menu card may render. Requires pricing, because that copy
   * interpolates two amounts.
   */
  canShowCard: boolean;
  /**
   * True when the dialog may render. Does *not* require pricing — per the design
   * neither the approaching nor the reached modal quotes a price, so waiting for
   * a message that may never arrive would suppress the block warning too.
   */
  canShowDialog: boolean;
  /** Whether the dialog is currently open. */
  isDialogOpen: boolean;
  /**
   * Records that the dialog was shown, satisfying the once-per-session rule.
   *
   * Called by the dialog as it mounts rather than by the effect that opens it:
   * marking before anything rendered meant a dialog that failed to render burned
   * the session and never retried.
   */
  markDialogShown: () => void;
  closeDialog: () => void;
  /** Sends the merchant to checkout. No-op when there is no URL. */
  goToCheckout: () => void;
}

const BookingsUpgradeContext = createContext<
  BookingsUpgradeContextValue | undefined
>(undefined);

/**
 * Records that the dialog has been shown for a given state this session.
 *
 * Keyed by state rather than a single flag: a merchant who crosses 20 and then
 * hits 25 in one session should still be told they are blocked. Within one state
 * it fires once — reaching 21 after seeing the dialog at 20 does not re-open it.
 */
function readSeenStates(): string[] {
  try {
    const raw = window.sessionStorage.getItem(DIALOG_SEEN_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function markStateSeen(state: string): void {
  try {
    const seen = readSeenStates();
    if (!seen.includes(state)) {
      window.sessionStorage.setItem(
        DIALOG_SEEN_STORAGE_KEY,
        JSON.stringify([...seen, state])
      );
    }
  } catch {
    // sessionStorage unavailable — the dialog then shows on every trigger,
    // which is noisier but not broken.
  }
}

export function BookingsUpgradeProvider({ children }: { children: ReactNode }) {
  const source = useAppSelector((state) => state.currentMerchant.source);
  const appointmentUsage = useAppSelector(
    (state) => state.currentMerchant.appointmentUsage
  );

  // Hook rather than isBetaToggleEnabled so the React surfaces react to the
  // switch on /beta without a reload.
  const [isBetaEnabled] = useLocalStorageState<boolean>(
    BETA_TOGGLES.bookingsUpgrade.key,
    { defaultValue: false, listenStorageChange: true }
  );

  const [pricing, setPricing] = useState<UpgradePricing | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Shared with getAppSettings so the shell's surfaces and the flag handed to
  // the Elm micro-apps always agree on eligibility and on the count.
  const eligibility = useMemo(
    () =>
      resolveUpgradeEligibility(
        { source, appointmentUsage },
        Boolean(isBetaEnabled)
      ),
    [source, appointmentUsage, isBetaEnabled]
  );

  const upgradeUrl = eligibility?.upgradeUrl ?? null;

  // Pricing from the One.com parent window.
  const handleMessage = useCallback(
    ({
      message,
      event,
    }: {
      message: { type: string; data?: unknown };
      event: MessageEvent;
    }) => {
      // `usePostMessageChannel` hands over every `message` event on the window,
      // so anything running in the page could otherwise set the price the card
      // quotes. Pinned to the sender rather than the origin because the allowed
      // origins are not known yet — see the TODO in that hook. Off an iframe
      // `window.parent` is `window`, which keeps the playground working.
      if (event.source !== window.parent) return;

      if (message.type !== PRICING_MESSAGE_TYPE) return;

      const parsed = parsePricing(message.data);
      if (parsed) setPricing(parsed);
    },
    []
  );

  const { sendMessageToParentWindow } = usePostMessageChannel({
    onMessage: handleMessage,
  });

  // Ask the parent for the price, rather than waiting to be told.
  //
  // Only once the upsell applies: for every other merchant nothing would be
  // rendered with the answer. Asked once — the `pricing` guard keeps a later
  // re-run from repeating it.
  useEffect(() => {
    if (!eligibility || pricing) return;

    sendMessageToParentWindow(PRICING_REQUEST_MESSAGE_TYPE);
  }, [eligibility, pricing, sendMessageToParentWindow]);

  const usage: AppointmentUsage | null = eligibility
    ? {
        used: eligibility.used,
        limit: eligibility.limit,
        approachingLimit: eligibility.approachingLimit,
        bookingAllowed: eligibility.bookingAllowed,
      }
    : null;

  const usageState: UsageState = usage ? classifyUsage(usage) : 'under';

  // The dialog's whole point is its "Continue to payment" button, so it stays
  // shut without a URL to send the merchant to. (The Elm banners differ: they
  // still inform, just without the button.)
  const canShowDialog = Boolean(usage && upgradeUrl && usageState !== 'under');

  const canShowCard = Boolean(canShowDialog && pricing);

  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  const goToCheckout = useCallback(() => {
    if (!upgradeUrl) return;
    // Top-level navigation, not `window.open`: app-shell runs inside the One.com
    // iframe, and a popup would strand the buyer in a window with no shell
    // chrome. `window.top` so the parent navigates too.
    (window.top ?? window).location.href = upgradeUrl;
  }, [upgradeUrl]);

  const markDialogShown = useCallback(() => {
    markStateSeen(usageState);
  }, [usageState]);

  // Auto-open, covering both trigger points the design specifies:
  //
  //   "on login"    — runs once usage first resolves, on the screen the merchant
  //                   lands on.
  //   "mid-session" — runs again when `usageState` changes, which happens as the
  //                   allowance is re-read while appointments are created (see
  //                   subscriptions/observeAppointmentUsage).
  //
  // The once-per-session guard keeps the two from double-firing.
  useEffect(() => {
    if (!canShowDialog) return;
    if (readSeenStates().includes(usageState)) return;

    setIsDialogOpen(true);
  }, [canShowDialog, usageState]);

  const value = useMemo<BookingsUpgradeContextValue>(
    () => ({
      pricing,
      usage,
      usageState,
      canShowCard,
      canShowDialog,
      isDialogOpen,
      markDialogShown,
      closeDialog,
      goToCheckout,
    }),
    [
      pricing,
      usage,
      usageState,
      canShowCard,
      canShowDialog,
      isDialogOpen,
      markDialogShown,
      closeDialog,
      goToCheckout,
    ]
  );

  return (
    <BookingsUpgradeContext.Provider value={value}>
      {children}
    </BookingsUpgradeContext.Provider>
  );
}

export function useBookingsUpgrade(): BookingsUpgradeContextValue {
  const context = useContext(BookingsUpgradeContext);

  if (!context) {
    throw new Error(
      'useBookingsUpgrade must be used within a BookingsUpgradeProvider'
    );
  }

  return context;
}
